import React, { useMemo, useState } from 'react'
import {
  useSequencerStore,
  selectCurrentBuses,
  MASTER_BUS_ID,
} from '@/stores/sequencerStore'
import { useAudioDevicesStore } from '@/stores/audioDevicesStore'
import { useMidiMappable } from '@/hooks/useMidiMappable'
import { BusEffectChain } from '@/components/werkshop/BusEffectChain'

/**
 * Vertical mixer strip list — one per bus. Ships with volume + pan
 * only (Phase 2b); per-bus FX chains arrive in Phase 2c. The master
 * bus is pinned to the leftmost strip and can't be renamed / deleted;
 * user buses show name-editing + delete affordances.
 *
 * Each fader opts in to MIDI mapping so an external controller can
 * drive volumes for live mixing — `bus.<id>.volume` / `bus.<id>.pan`.
 */
export const BusRack: React.FC = () => {
  const buses = useSequencerStore(selectCurrentBuses)
  const addBus = useSequencerStore(s => s.addBus)

  return (
    <section className="bg-surface-raised border border-brd-subtle rounded-xl p-3 flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">
            Mixer
          </h4>
          <p className="text-[10px] text-content-tertiary mt-0.5">
            Group pads into mixer strips. Each strip has its own volume + pan;
            the Master strip is always present and everything feeds into it
            unless a strip is routed to a specific output pair.
          </p>
        </div>
        <button
          type="button"
          onClick={() => addBus()}
          className="text-[10px] font-mono rounded-md border border-brd-subtle px-2 py-1 text-content-secondary hover:text-accent hover:border-[color:var(--accent-halo)] transition-colors"
        >
          + Add bus
        </button>
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
        {buses.map(bus => (
          <BusStrip key={bus.id} busId={bus.id} />
        ))}
      </div>
    </section>
  )
}

const BusStrip: React.FC<{ busId: string }> = ({ busId }) => {
  const bus = useSequencerStore(s =>
    selectCurrentBuses(s).find(b => b.id === busId),
  )
  const setBusVolume = useSequencerStore(s => s.setBusVolume)
  const setBusPan = useSequencerStore(s => s.setBusPan)
  const setBusOutputChannel = useSequencerStore(s => s.setBusOutputChannel)
  const renameBus = useSequencerStore(s => s.renameBus)
  const removeBus = useSequencerStore(s => s.removeBus)
  const outputs = useAudioDevicesStore(s => s.outputs)
  const selectedOutputId = useAudioDevicesStore(s => s.selectedOutputId)
  const [editingName, setEditingName] = useState(false)
  const [draftName, setDraftName] = useState('')

  // Stereo channel pairs supported by the active output device.
  // Always include 1/2 so users can pick "back to default" even when
  // the device later downgrades. Cap at 32 to match the merger
  // allocation in `busRegistry.ts`; devices beyond that are exotic
  // and the UI tolerates picking a slot the hardware can't address
  // (engine silently drops it).
  const channelPairs = useMemo(() => {
    const device = outputs.find(o => o.id === selectedOutputId)
    const rawCount = device?.channelCount ?? 0
    const count = rawCount > 0 ? Math.min(rawCount, 32) : 2
    const pairs: Array<{ start: number; label: string }> = []
    for (let i = 0; i + 1 < count; i += 2) {
      pairs.push({ start: i, label: `${i + 1}/${i + 2}` })
    }
    if (pairs.length === 0) pairs.push({ start: 0, label: '1/2' })
    return pairs
  }, [outputs, selectedOutputId])

  const volumeMap = useMidiMappable<HTMLInputElement>({
    id: `bus.${busId}.volume`,
    kind: 'continuous',
    label: bus ? `${bus.name} — Volume` : `Bus ${busId} volume`,
    min: 0,
    max: 1,
    setValue: v => {
      if (typeof v === 'number') setBusVolume(busId, v)
    },
  })
  const panMap = useMidiMappable<HTMLInputElement>({
    id: `bus.${busId}.pan`,
    kind: 'continuous',
    label: bus ? `${bus.name} — Pan` : `Bus ${busId} pan`,
    min: -1,
    max: 1,
    setValue: v => {
      if (typeof v === 'number') setBusPan(busId, v)
    },
  })

  if (!bus) return null
  const isMaster = bus.id === MASTER_BUS_ID

  const startRename = () => {
    if (isMaster) return
    setDraftName(bus.name)
    setEditingName(true)
  }
  const commitRename = () => {
    const t = draftName.trim()
    if (t && t !== bus.name) renameBus(busId, t)
    setEditingName(false)
  }

  return (
    <div
      className={`flex flex-col items-center gap-2 rounded-lg border p-2 min-w-[96px] flex-shrink-0 ${
        isMaster
          ? 'border-accent bg-accent-muted'
          : 'border-brd-subtle bg-surface-sunken'
      }`}
    >
      {editingName ? (
        <input
          autoFocus
          value={draftName}
          onChange={e => setDraftName(e.target.value)}
          onBlur={commitRename}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitRename()
            } else if (e.key === 'Escape') {
              setEditingName(false)
            }
          }}
          className="w-full text-[11px] font-mono bg-surface-raised border border-brd-subtle rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
        />
      ) : (
        <button
          type="button"
          onClick={startRename}
          className={`w-full truncate text-[11px] font-mono text-center ${
            isMaster
              ? 'text-accent cursor-default'
              : 'text-content-primary hover:text-accent'
          }`}
          title={isMaster ? 'Master bus' : 'Click to rename'}
        >
          {bus.name}
        </button>
      )}

      <div className="flex flex-col items-center gap-1 w-full">
        <span className="text-[9px] font-mono text-content-tertiary tabular-nums">
          {Math.round(bus.volume * 100)}
        </span>
        <input
          ref={volumeMap.ref}
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(bus.volume * 100)}
          onChange={e => setBusVolume(busId, Number(e.target.value) / 100)}
          onMouseDown={e => {
            if (volumeMap.armForLearn(e)) e.currentTarget.blur()
          }}
          className="w-full accent-[color:var(--accent)]"
          title="Volume"
        />
      </div>

      <div className="flex flex-col items-center gap-1 w-full">
        <span className="text-[9px] font-mono text-content-tertiary tabular-nums">
          {bus.pan === 0
            ? 'C'
            : bus.pan > 0
              ? `R${Math.round(bus.pan * 100)}`
              : `L${Math.round(-bus.pan * 100)}`}
        </span>
        <input
          ref={panMap.ref}
          type="range"
          min={-100}
          max={100}
          step={1}
          value={Math.round(bus.pan * 100)}
          onChange={e => setBusPan(busId, Number(e.target.value) / 100)}
          onMouseDown={e => {
            if (panMap.armForLearn(e)) e.currentTarget.blur()
          }}
          className="w-full accent-[color:var(--accent)]"
          title="Pan"
        />
      </div>

      {!isMaster && channelPairs.length > 1 && (
        <label
          className="flex flex-col items-center gap-0.5 w-full"
          title="Route this bus's stereo output to a specific pair of hardware channels on the active output device. Channels beyond 1/2 bypass the master FX chain so monitor / outboard sends stay clean."
        >
          <span className="text-[9px] font-mono uppercase tracking-wider text-content-tertiary">
            Out
          </span>
          <select
            value={bus.outputChannelStart ?? 0}
            onChange={e => setBusOutputChannel(busId, Number(e.target.value))}
            className="w-full rounded border border-brd-subtle bg-surface-raised px-1 py-0.5 text-[10px] font-mono text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
          >
            {channelPairs.map(p => (
              <option key={p.start} value={p.start}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {!isMaster && <BusEffectChain busId={busId} />}

      {!isMaster && (
        <button
          type="button"
          onClick={() => removeBus(busId)}
          className="text-[9px] font-mono text-content-tertiary hover:text-destructive"
          title={`Delete ${bus.name}`}
        >
          Remove
        </button>
      )}
    </div>
  )
}
